import { useCallback, useMemo } from 'react';
import { useProjectStore } from '@/store/projectStore';
import { HexColorPicker } from 'react-colorful';
import { rgbaToHex, hexToRgba, formatTime, createDefaultAnimationProperties } from '@/types';
import type {
  OverlayElement,
  ShapeOverlay,
  TextOverlay,
  BlurOverlay,
  ImageOverlay,
  RGBA,
  AnimationType,
} from '@/types';
import clsx from 'clsx';
import {
  Square,
  Circle,
  Type,
  Image,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  ChevronsDown,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Grid,
  Wand2,
} from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import { createDefaultTileProperties } from '@/types';
import { ANIMATION_TYPES, getAnimationLabel } from '@/utils/animationUtils';

interface ColorPickerProps {
  label: string;
  color: RGBA;
  onChange: (color: RGBA) => void;
}

const ColorPicker = ({ label, color, onChange }: ColorPickerProps) => {
  const hex = rgbaToHex(color);
  
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-editor-text-muted">{label}</span>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            className="w-10 h-10 rounded border-2 border-editor-border shadow-md relative overflow-hidden"
            style={{ backgroundColor: `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})` }}
          >
            {/* Checkerboard background to show transparency */}
            <div 
              className="absolute inset-0 -z-10" 
              style={{
                backgroundImage: 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)',
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
              }}
            />
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="bg-editor-panel border border-editor-border rounded-lg p-4 z-50 animate-fade-in shadow-xl">
            <HexColorPicker
              color={hex}
              onChange={(newHex) => onChange(hexToRgba(newHex, color.a))}
            />
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs text-editor-text-muted">Color Alpha</label>
                <span className="text-xs text-editor-text font-mono">{Math.round(color.a * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={color.a}
                onChange={(e) => onChange({ ...color, a: parseFloat(e.target.value) })}
                className="w-full"
              />
              <p className="text-[10px] text-editor-text-dim">
                Tip: Set to 100% for solid fill
              </p>
            </div>
            <Popover.Arrow className="fill-editor-border" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

interface NumberInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
}

const NumberInput = ({ label, value, onChange, min, max, step = 1, unit }: NumberInputProps) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-editor-text-muted">{label}</span>
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={Math.round(value * 100) / 100}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        className="w-20 text-right text-xs"
      />
      {unit && <span className="text-xs text-editor-text-dim w-6">{unit}</span>}
    </div>
  </div>
);

interface FlexibleTimeInputProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  max: number;
}

const FlexibleTimeInput = ({ label, value, onChange, max }: FlexibleTimeInputProps) => {
  // Convert seconds to hours, minutes, seconds, milliseconds
  const totalMs = Math.round(value * 1000);
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const milliseconds = totalMs % 1000;
  
  const handleChange = (h: number, m: number, s: number, ms: number) => {
    const newValue = h * 3600 + m * 60 + s + ms / 1000;
    onChange(Math.max(0, Math.min(max, newValue)));
  };

  const inputStyle = "w-14 h-9 text-center text-sm font-mono bg-editor-surface border border-editor-border rounded text-white focus:border-editor-accent focus:ring-1 focus:ring-editor-accent";
  
  return (
    <div className="space-y-2">
      <span className="text-xs text-editor-text-muted font-medium block">{label}</span>
      <div className="flex items-start gap-1">
        {/* Hours */}
        <div className="flex flex-col items-center">
          <input
            type="number"
            value={hours.toString().padStart(2, '0')}
            onChange={(e) => handleChange(parseInt(e.target.value) || 0, minutes, seconds, milliseconds)}
            min={0}
            max={99}
            className={inputStyle}
          />
          <span className="text-[10px] text-editor-text-muted mt-1">hrs</span>
        </div>
        <span className="text-editor-text-muted text-lg font-bold mt-1.5">:</span>
        {/* Minutes */}
        <div className="flex flex-col items-center">
          <input
            type="number"
            value={minutes.toString().padStart(2, '0')}
            onChange={(e) => handleChange(hours, Math.min(59, parseInt(e.target.value) || 0), seconds, milliseconds)}
            min={0}
            max={59}
            className={inputStyle}
          />
          <span className="text-[10px] text-editor-text-muted mt-1">min</span>
        </div>
        <span className="text-editor-text-muted text-lg font-bold mt-1.5">:</span>
        {/* Seconds */}
        <div className="flex flex-col items-center">
          <input
            type="number"
            value={seconds.toString().padStart(2, '0')}
            onChange={(e) => handleChange(hours, minutes, Math.min(59, parseInt(e.target.value) || 0), milliseconds)}
            min={0}
            max={59}
            className={inputStyle}
          />
          <span className="text-[10px] text-editor-text-muted mt-1">sec</span>
        </div>
        <span className="text-editor-text-muted text-lg font-bold mt-1.5">.</span>
        {/* Milliseconds */}
        <div className="flex flex-col items-center">
          <input
            type="number"
            value={milliseconds.toString().padStart(3, '0')}
            onChange={(e) => handleChange(hours, minutes, seconds, Math.min(999, parseInt(e.target.value) || 0))}
            min={0}
            max={999}
            step={10}
            className="w-16 h-9 text-center text-sm font-mono bg-editor-surface border border-editor-border rounded text-white focus:border-editor-accent focus:ring-1 focus:ring-editor-accent"
          />
          <span className="text-[10px] text-editor-text-muted mt-1">ms</span>
        </div>
      </div>
    </div>
  );
};

const getOverlayIcon = (type: string) => {
  switch (type) {
    case 'rectangle':
    case 'rounded-rectangle':
      return <Square size={16} />;
    case 'circle':
    case 'ellipse':
      return <Circle size={16} />;
    case 'text':
      return <Type size={16} />;
    case 'image':
      return <Image size={16} />;
    case 'blur':
      return <Sparkles size={16} />;
    default:
      return <Square size={16} />;
  }
};

export const OverlayInspector = () => {
  const video = useProjectStore((state) => state.project.video);
  const overlays = useProjectStore((state) => state.project.overlays);
  const selectedIds = useProjectStore((state) => state.selectionState.selectedIds);
  const updateOverlay = useProjectStore((state) => state.updateOverlay);
  
  // Get selected overlay
  const selectedOverlay = useMemo(() => {
    if (selectedIds.length !== 1) return null;
    return overlays.find((o) => o.id === selectedIds[0]) ?? null;
  }, [overlays, selectedIds]);
  
  // Update handlers with history
  const handleUpdate = useCallback((updates: Partial<OverlayElement>) => {
    if (!selectedOverlay) return;
    updateOverlay(selectedOverlay.id, updates);
  }, [selectedOverlay, updateOverlay]);
  
  if (!video) {
    return (
      <div className="w-72 glass-panel border-l border-white/5 flex items-center justify-center p-4">
        <p className="text-sm text-editor-text-muted text-center">
          Import a video to start editing
        </p>
      </div>
    );
  }
  
  if (!selectedOverlay) {
    return (
      <div className="w-72 glass-panel border-l border-white/5 p-4">
        <h3 className="text-sm font-medium text-editor-text mb-4">Properties</h3>
        <p className="text-xs text-editor-text-muted text-center py-8">
          Select an overlay to edit its properties
        </p>
      </div>
    );
  }
  
  return (
    <div className="w-72 glass-panel border-l border-white/5 overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-editor-border">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 bg-editor-accent rounded">
            {getOverlayIcon(selectedOverlay.type)}
          </div>
          <div className="flex-1">
            <input
              type="text"
              value={selectedOverlay.name}
              onChange={(e) => handleUpdate({ name: e.target.value })}
              className="w-full bg-transparent border-none text-sm font-medium p-0"
            />
            <p className="text-xs text-editor-text-muted capitalize">
              {selectedOverlay.type.replace('-', ' ')}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleUpdate({ visible: !selectedOverlay.visible })}
            className={clsx(
              'flex-1 btn btn-ghost text-xs',
              !selectedOverlay.visible && 'text-editor-text-dim'
            )}
          >
            {selectedOverlay.visible ? <Eye size={14} /> : <EyeOff size={14} />}
            <span className="ml-1">{selectedOverlay.visible ? 'Visible' : 'Hidden'}</span>
          </button>
          <button
            onClick={() => handleUpdate({ locked: !selectedOverlay.locked })}
            className={clsx(
              'flex-1 btn btn-ghost text-xs',
              selectedOverlay.locked && 'text-editor-warning'
            )}
          >
            {selectedOverlay.locked ? <Lock size={14} /> : <Unlock size={14} />}
            <span className="ml-1">{selectedOverlay.locked ? 'Locked' : 'Unlocked'}</span>
          </button>
        </div>
      </div>
      
      {/* Animation Effects */}
      <div className="p-4 border-b border-editor-border">
        <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3 flex items-center gap-2">
          <Wand2 size={14} /> Animation Effects
        </h4>
        
        <div className="space-y-4">
          {/* Entry Animation */}
          <div className="space-y-2">
            <label className="text-xs text-editor-text-muted">Entry Animation</label>
            <div className="flex gap-2">
              <select
                value={selectedOverlay.animation?.entryType || 'none'}
                onChange={(e) => {
                  const currentAnim = selectedOverlay.animation || createDefaultAnimationProperties();
                  handleUpdate({
                    animation: { ...currentAnim, entryType: e.target.value as AnimationType }
                  });
                }}
                className="flex-1 bg-editor-surface border border-editor-border rounded-lg px-3 py-2 text-sm text-editor-text"
              >
                {ANIMATION_TYPES.map((type) => (
                  <option key={type} value={type}>{getAnimationLabel(type)}</option>
                ))}
              </select>
              <input
                type="number"
                min="0.1"
                max="3"
                step="0.1"
                value={selectedOverlay.animation?.entryDuration || 0.5}
                onChange={(e) => {
                  const currentAnim = selectedOverlay.animation || createDefaultAnimationProperties();
                  handleUpdate({
                    animation: { ...currentAnim, entryDuration: parseFloat(e.target.value) || 0.5 }
                  });
                }}
                className="w-16 bg-editor-surface border border-editor-border rounded-lg px-2 py-2 text-sm text-editor-text text-center"
                title="Duration (seconds)"
              />
              <span className="text-xs text-editor-text-muted self-center">sec</span>
            </div>
          </div>
          
          {/* Exit Animation */}
          <div className="space-y-2">
            <label className="text-xs text-editor-text-muted">Exit Animation</label>
            <div className="flex gap-2">
              <select
                value={selectedOverlay.animation?.exitType || 'none'}
                onChange={(e) => {
                  const currentAnim = selectedOverlay.animation || createDefaultAnimationProperties();
                  handleUpdate({
                    animation: { ...currentAnim, exitType: e.target.value as AnimationType }
                  });
                }}
                className="flex-1 bg-editor-surface border border-editor-border rounded-lg px-3 py-2 text-sm text-editor-text"
              >
                {ANIMATION_TYPES.map((type) => (
                  <option key={type} value={type}>{getAnimationLabel(type)}</option>
                ))}
              </select>
              <input
                type="number"
                min="0.1"
                max="3"
                step="0.1"
                value={selectedOverlay.animation?.exitDuration || 0.5}
                onChange={(e) => {
                  const currentAnim = selectedOverlay.animation || createDefaultAnimationProperties();
                  handleUpdate({
                    animation: { ...currentAnim, exitDuration: parseFloat(e.target.value) || 0.5 }
                  });
                }}
                className="w-16 bg-editor-surface border border-editor-border rounded-lg px-2 py-2 text-sm text-editor-text text-center"
                title="Duration (seconds)"
              />
              <span className="text-xs text-editor-text-muted self-center">sec</span>
            </div>
          </div>
          
          {/* Pulse Animation */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-sm text-editor-text">Pulse Effect</span>
              <span className="text-[10px] text-editor-text-muted">Subtle breathing animation</span>
            </div>
            <button
              onClick={() => {
                const currentAnim = selectedOverlay.animation || createDefaultAnimationProperties();
                handleUpdate({
                  animation: { ...currentAnim, pulseEnabled: !currentAnim.pulseEnabled }
                });
              }}
              className={clsx(
                'w-11 h-6 rounded-full transition-colors relative',
                selectedOverlay.animation?.pulseEnabled ? 'bg-editor-accent' : 'bg-editor-border'
              )}
            >
              <span
                className={clsx(
                  'absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform',
                  selectedOverlay.animation?.pulseEnabled && 'translate-x-5'
                )}
              />
            </button>
          </div>
        </div>
      </div>
      
      {/* Tiling - for Text and Image only */}
      {(selectedOverlay.type === 'text' || selectedOverlay.type === 'image') && (
        <div className="p-4 border-b border-editor-border">
          <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3 flex items-center gap-2">
            <Grid size={14} /> Tiling Pattern
          </h4>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-editor-text">Enable Tiling</span>
              <button
                onClick={() => {
                  const currentTile = (selectedOverlay as TextOverlay | ImageOverlay).tile;
                  const newTile = currentTile 
                    ? { ...currentTile, enabled: !currentTile.enabled }
                    : { ...createDefaultTileProperties(), enabled: true };
                    
                  handleUpdate({ tile: newTile } as Partial<TextOverlay | ImageOverlay>);
                }}
                className={clsx(
                  'w-11 h-6 rounded-full transition-colors relative',
                  (selectedOverlay as TextOverlay | ImageOverlay).tile?.enabled ? 'bg-editor-accent' : 'bg-editor-border'
                )}
              >
                <div
                  className={clsx(
                    'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                    (selectedOverlay as TextOverlay | ImageOverlay).tile?.enabled ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
            </div>

            {(selectedOverlay as TextOverlay | ImageOverlay).tile?.enabled && (
              <div className="space-y-3 animate-fade-in pl-2 border-l-2 border-editor-border ml-1">
                <NumberInput
                  label="Gap"
                  value={(selectedOverlay as TextOverlay | ImageOverlay).tile?.gap ?? 100}
                  onChange={(gap) => {
                    const currentTile = (selectedOverlay as TextOverlay | ImageOverlay).tile || createDefaultTileProperties();
                    handleUpdate({ tile: { ...currentTile, gap } } as Partial<TextOverlay | ImageOverlay>);
                  }}
                  min={0}
                  max={500}
                  unit="px"
                />
                <NumberInput
                  label="Angle"
                  value={(selectedOverlay as TextOverlay | ImageOverlay).tile?.angle ?? 0}
                  onChange={(angle) => {
                    const currentTile = (selectedOverlay as TextOverlay | ImageOverlay).tile || createDefaultTileProperties();
                    handleUpdate({ tile: { ...currentTile, angle } } as Partial<TextOverlay | ImageOverlay>);
                  }}
                  min={-360}
                  max={360}
                  unit="°"
                />
                
                 {/* Opacity Override */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-editor-text-muted">Pattern Opacity</span>
                    <span className="text-xs text-editor-text font-mono bg-editor-surface px-2 py-0.5 rounded">
                        {(selectedOverlay as TextOverlay | ImageOverlay).tile?.opacity ?? selectedOverlay.style.opacity}%
                    </span>
                    </div>
                    <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={(selectedOverlay as TextOverlay | ImageOverlay).tile?.opacity ?? selectedOverlay.style.opacity}
                    onChange={(e) => {
                         const currentTile = (selectedOverlay as TextOverlay | ImageOverlay).tile || createDefaultTileProperties();
                        handleUpdate({ tile: { ...currentTile, opacity: parseInt(e.target.value) } } as Partial<TextOverlay | ImageOverlay>);
                    }}
                    className="w-full"
                    />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Transform */}
      <div className="p-4 border-b border-editor-border">
        <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
          Transform
        </h4>
        <div className="space-y-2">
          <NumberInput
            label="X"
            value={selectedOverlay.bounds.x}
            onChange={(x) => handleUpdate({ bounds: { ...selectedOverlay.bounds, x } })}
            unit="px"
          />
          <NumberInput
            label="Y"
            value={selectedOverlay.bounds.y}
            onChange={(y) => handleUpdate({ bounds: { ...selectedOverlay.bounds, y } })}
            unit="px"
          />
          <NumberInput
            label="Width"
            value={selectedOverlay.bounds.width}
            onChange={(width) => handleUpdate({ bounds: { ...selectedOverlay.bounds, width: Math.max(10, width) } })}
            min={10}
            unit="px"
          />
          <NumberInput
            label="Height"
            value={selectedOverlay.bounds.height}
            onChange={(height) => handleUpdate({ bounds: { ...selectedOverlay.bounds, height: Math.max(10, height) } })}
            min={10}
            unit="px"
          />
          <NumberInput
            label="Rotation"
            value={selectedOverlay.rotation}
            onChange={(rotation) => handleUpdate({ rotation: rotation % 360 })}
            min={0}
            max={360}
            unit="°"
          />
        </div>
      </div>
      
      {/* Timing */}
      <div className="p-4 border-b border-editor-border">
        <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
          Timing
        </h4>
        <div className="space-y-4">
          <FlexibleTimeInput
            label="Start Time"
            value={selectedOverlay.startTime}
            onChange={(startTime) => handleUpdate({ startTime })}
            max={video.duration}
          />
          <FlexibleTimeInput
            label="End Time"
            value={selectedOverlay.endTime}
            onChange={(endTime) => handleUpdate({ endTime })}
            max={video.duration}
          />
          <div className="flex items-center justify-between pt-2 border-t border-editor-border">
            <span className="text-xs text-editor-text-muted">Total Duration</span>
            <span className="text-xs text-editor-text font-mono bg-editor-surface px-2 py-1 rounded">
              {formatTime(selectedOverlay.endTime - selectedOverlay.startTime)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-editor-text-muted">Video Length</span>
            <span className="text-xs text-editor-text-dim font-mono">
              {formatTime(video.duration)}
            </span>
          </div>
        </div>
      </div>
      
      {/* Style */}
      <div className="p-4 border-b border-editor-border">
        <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
          Style
        </h4>
        <div className="space-y-3">
          {/* Fill Color - for shapes and text only */}
          {selectedOverlay.type !== 'blur' && selectedOverlay.type !== 'image' && (
            <ColorPicker
              label={selectedOverlay.type === 'text' ? 'Text Color' : 'Fill Color'}
              color={selectedOverlay.style.fillColor}
              onChange={(fillColor) => handleUpdate({ style: { ...selectedOverlay.style, fillColor } })}
            />
          )}
          
          {/* Border - for shapes only */}
          {selectedOverlay.type !== 'text' && selectedOverlay.type !== 'image' && selectedOverlay.type !== 'blur' && (
            <>
              <ColorPicker
                label="Border Color"
                color={selectedOverlay.style.borderColor}
                onChange={(borderColor) => handleUpdate({ style: { ...selectedOverlay.style, borderColor } })}
              />
              <NumberInput
                label="Border Width"
                value={selectedOverlay.style.borderWidth}
                onChange={(borderWidth) => handleUpdate({ style: { ...selectedOverlay.style, borderWidth } })}
                min={0}
                max={20}
                unit="px"
              />
            </>
          )}
          
          {/* Opacity - for all types */}
          <div className="pt-2 border-t border-editor-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-editor-text-muted">Overall Opacity</span>
              <span className="text-xs text-editor-text font-mono bg-editor-surface px-2 py-0.5 rounded">
                {selectedOverlay.style.opacity}%
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={selectedOverlay.style.opacity}
              onChange={(e) => handleUpdate({ style: { ...selectedOverlay.style, opacity: parseInt(e.target.value) } })}
              className="w-full"
            />
            <div className="flex justify-between text-[10px] text-editor-text-dim mt-1">
              <span>Transparent</span>
              <span>Solid</span>
            </div>
          </div>
          
          {/* Info for image overlays */}
          {selectedOverlay.type === 'image' && (
            <p className="text-[10px] text-editor-text-dim bg-editor-surface p-2 rounded">
              💡 Adjust opacity to make the image more or less transparent
            </p>
          )}
        </div>
      </div>
      
      {/* Text-specific properties */}
      {selectedOverlay.type === 'text' && (
        <div className="p-4 border-b border-editor-border">
          <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
            Text
          </h4>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-editor-text-muted block mb-1">Content</label>
              <textarea
                value={(selectedOverlay as TextOverlay).text.content}
                onChange={(e) => handleUpdate({
                  text: { ...(selectedOverlay as TextOverlay).text, content: e.target.value }
                } as Partial<TextOverlay>)}
                className="w-full h-20 text-sm resize-none"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-editor-text-muted">Font</span>
              <select
                value={(selectedOverlay as TextOverlay).text.fontFamily}
                onChange={(e) => handleUpdate({
                  text: { ...(selectedOverlay as TextOverlay).text, fontFamily: e.target.value }
                } as Partial<TextOverlay>)}
                className="bg-editor-surface border border-editor-border rounded px-2 py-1 text-xs"
              >
                <option value="Arial">Arial</option>
                <option value="Helvetica">Helvetica</option>
                <option value="Georgia">Georgia</option>
                <option value="Times New Roman">Times New Roman</option>
                <option value="Courier New">Courier New</option>
                <option value="Impact">Impact</option>
              </select>
            </div>
            <NumberInput
              label="Size"
              value={(selectedOverlay as TextOverlay).text.fontSize}
              onChange={(fontSize) => handleUpdate({
                text: { ...(selectedOverlay as TextOverlay).text, fontSize }
              } as Partial<TextOverlay>)}
              min={8}
              max={200}
              unit="px"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-editor-text-muted">Style</span>
              <div className="flex gap-1">
                <button
                  onClick={() => handleUpdate({
                    text: {
                      ...(selectedOverlay as TextOverlay).text,
                      fontWeight: (selectedOverlay as TextOverlay).text.fontWeight === 'bold' ? 'normal' : 'bold'
                    }
                  } as Partial<TextOverlay>)}
                  className={clsx(
                    'px-2 py-1 rounded text-xs font-bold',
                    (selectedOverlay as TextOverlay).text.fontWeight === 'bold'
                      ? 'bg-editor-accent text-white'
                      : 'bg-editor-surface text-editor-text-muted'
                  )}
                >
                  B
                </button>
                <button
                  onClick={() => handleUpdate({
                    text: {
                      ...(selectedOverlay as TextOverlay).text,
                      fontStyle: (selectedOverlay as TextOverlay).text.fontStyle === 'italic' ? 'normal' : 'italic'
                    }
                  } as Partial<TextOverlay>)}
                  className={clsx(
                    'px-2 py-1 rounded text-xs italic',
                    (selectedOverlay as TextOverlay).text.fontStyle === 'italic'
                      ? 'bg-editor-accent text-white'
                      : 'bg-editor-surface text-editor-text-muted'
                  )}
                >
                  I
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-editor-text-muted">Align</span>
              <div className="flex gap-1">
                {(['left', 'center', 'right'] as const).map((align) => (
                  <button
                    key={align}
                    onClick={() => handleUpdate({
                      text: { ...(selectedOverlay as TextOverlay).text, textAlign: align }
                    } as Partial<TextOverlay>)}
                    className={clsx(
                      'px-2 py-1 rounded text-xs capitalize',
                      (selectedOverlay as TextOverlay).text.textAlign === align
                        ? 'bg-editor-accent text-white'
                        : 'bg-editor-surface text-editor-text-muted'
                    )}
                  >
                    {align.charAt(0).toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blur-specific properties */}
      {selectedOverlay.type === 'blur' && (
        <div className="p-4 border-b border-editor-border">
          <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
            Effect
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-editor-text-muted">Type</span>
              <select
                value={(selectedOverlay as BlurOverlay).blur.blurType}
                onChange={(e) => handleUpdate({
                  blur: { ...(selectedOverlay as BlurOverlay).blur, blurType: e.target.value as 'blur' | 'pixelate' }
                } as Partial<BlurOverlay>)}
                className="bg-editor-surface border border-editor-border rounded px-2 py-1 text-xs"
              >
                <option value="blur">Blur</option>
                <option value="pixelate">Pixelate</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-editor-text-muted">Intensity</span>
                <span className="text-xs text-editor-text">
                  {(selectedOverlay as BlurOverlay).blur.intensity}%
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="100"
                value={(selectedOverlay as BlurOverlay).blur.intensity}
                onChange={(e) => handleUpdate({
                  blur: { ...(selectedOverlay as BlurOverlay).blur, intensity: parseInt(e.target.value) }
                } as Partial<BlurOverlay>)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Corner radius for rounded rectangles */}
      {selectedOverlay.type === 'rounded-rectangle' && (
        <div className="p-4 border-b border-editor-border">
          <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
            Shape
          </h4>
          <NumberInput
            label="Corner Radius"
            value={(selectedOverlay as ShapeOverlay).cornerRadius ?? 12}
            onChange={(cornerRadius) => handleUpdate({ cornerRadius } as Partial<ShapeOverlay>)}
            min={0}
            max={100}
            unit="px"
          />
        </div>
      )}
      
      {/* Layer Order */}
      <LayerOrderSection overlayId={selectedOverlay.id} zIndex={selectedOverlay.zIndex} totalOverlays={overlays.length} />
    </div>
  );
};

// Layer Order Section Component
const LayerOrderSection = ({ overlayId, zIndex, totalOverlays }: { overlayId: string; zIndex: number; totalOverlays: number }) => {
  const reorderOverlay = useProjectStore((state) => state.reorderOverlay);
  
  return (
    <div className="p-4 border-b border-editor-border">
      <h4 className="text-xs font-medium text-editor-text-muted uppercase tracking-wide mb-3">
        Layer Order
      </h4>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-editor-text-muted">Current Layer</span>
          <span className="text-xs text-editor-text font-mono bg-editor-surface px-2 py-0.5 rounded">
            {zIndex + 1} of {totalOverlays}
          </span>
        </div>
        <div className="grid grid-cols-4 gap-1">
          <button
            onClick={() => reorderOverlay(overlayId, 'top')}
            className="flex flex-col items-center justify-center p-2 rounded bg-editor-surface hover:bg-editor-hover transition-colors"
            title="Bring to Front"
            disabled={zIndex === totalOverlays - 1}
          >
            <ChevronsUp size={16} className={zIndex === totalOverlays - 1 ? 'text-editor-text-dim' : 'text-editor-text'} />
            <span className="text-[9px] text-editor-text-muted mt-1">Front</span>
          </button>
          <button
            onClick={() => reorderOverlay(overlayId, 'up')}
            className="flex flex-col items-center justify-center p-2 rounded bg-editor-surface hover:bg-editor-hover transition-colors"
            title="Bring Forward"
            disabled={zIndex === totalOverlays - 1}
          >
            <ChevronUp size={16} className={zIndex === totalOverlays - 1 ? 'text-editor-text-dim' : 'text-editor-text'} />
            <span className="text-[9px] text-editor-text-muted mt-1">Up</span>
          </button>
          <button
            onClick={() => reorderOverlay(overlayId, 'down')}
            className="flex flex-col items-center justify-center p-2 rounded bg-editor-surface hover:bg-editor-hover transition-colors"
            title="Send Backward"
            disabled={zIndex === 0}
          >
            <ChevronDown size={16} className={zIndex === 0 ? 'text-editor-text-dim' : 'text-editor-text'} />
            <span className="text-[9px] text-editor-text-muted mt-1">Down</span>
          </button>
          <button
            onClick={() => reorderOverlay(overlayId, 'bottom')}
            className="flex flex-col items-center justify-center p-2 rounded bg-editor-surface hover:bg-editor-hover transition-colors"
            title="Send to Back"
            disabled={zIndex === 0}
          >
            <ChevronsDown size={16} className={zIndex === 0 ? 'text-editor-text-dim' : 'text-editor-text'} />
            <span className="text-[9px] text-editor-text-muted mt-1">Back</span>
          </button>
        </div>
      </div>
    </div>
  );
};

